import { CheckIcon, ErrorIcon } from '@assets/icons';
import { TextInput } from '@components/form';
import { Group } from '@components/layout';
import { Tooltip } from '@components/tooltip';
import { Typography } from '@components/typography';
import { Chip, Stack, useTheme } from '@mui/material';
import {
  InputDetectionResult,
  ParsedMcpServer,
  convertMcpConfig,
  detectInputType,
  getInputTypeDescription,
  toBackendFormat,
} from '@utils/mcp-config';
import { useField } from 'formik';
import { useEffect, useRef, useState } from 'react';
import { ConfigExamplesBar } from './ConfigExamplesBar';
import { SecretsChipBar } from './SecretsChipBar';

const DEFAULT_CONFIG_TEMPLATE = `{
    "type": "stdio",
    "command": "npx",
    "args": ["-y", "@modelcontextprotocol/server-name"],
    "env": {
      "API_KEY": "\${secrets.MY_API_KEY}"
    }
  }`;

type McpServerConfigFieldProps = {
  name: string;
  organizationId: string;
  workspaceId?: string;
  showExamples?: boolean;
};

export const McpServerConfigField = ({
  name,
  organizationId,
  workspaceId,
  showExamples = false,
}: McpServerConfigFieldProps) => {
  const { palette } = useTheme();
  const configInputRef = useRef<HTMLTextAreaElement>(null);
  const configErrorRef = useRef<string | undefined>();
  const validate = () => configErrorRef.current;
  const [field, meta, { setValue, setTouched }] = useField<ParsedMcpServer[]>({ name, validate });
  const [detectedFormat, setDetectedFormat] = useState<InputDetectionResult | null>(null);
  const [inputString, setInputString] = useState<string>(
    field?.value?.[0] ? toBackendFormat(field.value[0]) : '',
  );

  useEffect(() => {
    setDetectedFormat(detectInputType(inputString));
    const result = convertMcpConfig(inputString);
    if (result.success) {
      configErrorRef.current = undefined;
      void setValue(result.servers);
    } else {
      configErrorRef.current = result.error;
      void setValue([]);
    }
  }, [inputString, setValue]);

  const insertAtCursor = (
    ref: string,
    textareaRef: React.RefObject<HTMLTextAreaElement | null>,
  ) => {
    const textarea = textareaRef.current;
    if (!textarea) {
      setInputString((prev) => prev + ref);
      return;
    }
    const start = textarea.selectionStart ?? field.value.length;
    const end = textarea.selectionEnd ?? start;
    const next = `${inputString.slice(0, start)}${ref}${inputString.slice(end)}`;
    setInputString(next);

    const cursorPos = start + ref.length;
    requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(cursorPos, cursorPos);
    });
  };

  const handleInsertSecret = (ref: string) => insertAtCursor(ref, configInputRef);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    void setTouched(true);
    setInputString(e.target.value);
  };

  const handleExampleAdd = (example: string) => {
    insertAtCursor(example, configInputRef);
  };

  return (
    <Stack gap={1} mb={5}>
      {showExamples && <ConfigExamplesBar onExampleClick={handleExampleAdd} />}
      <Group alignItems="center" gap={1} justifyContent="space-between">
        <Typography
          className="deckard-text-input-label"
          component="label"
          htmlFor={name}
          variant="caption2"
          color={palette.input.label.default}
        >
          Configuration{showExamples ? '' : ' (JSON)'}
        </Typography>
        {detectedFormat && detectedFormat.type !== 'unknown' ? (
          <Tooltip title={meta.error}>
            <Chip
              icon={
                meta.error ? (
                  <ErrorIcon size={14} color={palette.error.main} />
                ) : detectedFormat.confidence === 'high' ? (
                  <CheckIcon size={14} color={palette.success.main} circle />
                ) : undefined
              }
              label={getInputTypeDescription(detectedFormat)}
              size="small"
              sx={{
                height: 24,
                fontSize: '0.75rem',
                alignSelf: 'flex-start',
                '& .MuiChip-label': {
                  color: meta.error
                    ? palette.error.main
                    : detectedFormat.confidence === 'high'
                      ? palette.success.main
                      : palette.warning.main,
                },
              }}
            />
          </Tooltip>
        ) : (
          meta.touched &&
          meta.error && (
            <Tooltip title={meta.error}>
              <Chip
                icon={
                  meta.error ? (
                    <ErrorIcon size={14} color={palette.error.main} />
                  ) : detectedFormat.confidence === 'high' ? (
                    <CheckIcon size={14} color={palette.success.main} circle />
                  ) : undefined
                }
                label="Invalid configuration"
                size="small"
                sx={{
                  height: 24,
                  fontSize: '0.75rem',
                  alignSelf: 'flex-start',
                  '& .MuiChip-label': {
                    color: meta.error
                      ? palette.error.main
                      : detectedFormat.confidence === 'high'
                        ? palette.success.main
                        : palette.warning.main,
                  },
                }}
              />
            </Tooltip>
          )
        )}
      </Group>
      <TextInput
        value={inputString}
        onChange={handleChange}
        multiline
        rows={9}
        placeholder={showExamples ? 'Paste config or command here...' : DEFAULT_CONFIG_TEMPLATE}
        inputRef={configInputRef}
        sx={{
          fontFamily: 'monospace',
          fontSize: '0.85rem',
        }}
      />
      {organizationId && (
        <SecretsChipBar
          organizationId={organizationId}
          workspaceId={workspaceId}
          onInsertSecret={handleInsertSecret}
        />
      )}
    </Stack>
  );
};
